"use client"
import {
  FlexCenter,
  Modal,
  ModalBody,
  ModalFooter,
  ModalFooterButton,
  ModalHeader,
  ModalHeaderTitle,
  type ModalProps,
  TextBody,
  TextDisplay,
  VisuallyHidden,
} from "@opensea/ui-kit"
import { Error } from "@opensea/ui-kit/icons"
import { bn } from "@opensea/utils"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { LISTING_MAX_DECIMALS } from "../../../constants"
import type { ListingFlowFormValues } from "../../types"

type ListingConfirmPriceModalProps = Pick<ModalProps, "open"> & {
  setIsOpen: (isOpen: boolean) => void
  onNext: () => void
}

export const ListingConfirmPriceModal = ({
  open,
  onNext,
  setIsOpen,
}: ListingConfirmPriceModalProps) => {
  const t = useTranslations("wallet.ListingConfirmPriceModal")
  const { control } = useFormContext<ListingFlowFormValues>()
  const listings = useWatch({ control, name: "listings" })
  const highSevPriceWarnings = listings.filter(
    listing => listing.shouldConfirmPrice,
  )
  const listingSymbol = listings[0].listingSymbol

  if (highSevPriceWarnings.length === 0) {
    return null
  }

  return (
    <Modal
      content={
        <>
          <ModalHeader>
            <VisuallyHidden>
              <ModalHeaderTitle>{t("titleVisuallyHidden")}</ModalHeaderTitle>
            </VisuallyHidden>
          </ModalHeader>
          <ModalBody className="flex-col items-center gap-3">
            <FlexCenter className="rounded-full bg-error-1-transparent-1 p-3">
              <Error color="error-1" size={28} />
            </FlexCenter>
            <TextDisplay size="sm">{t("title")}</TextDisplay>
            <TextBody className="text-center text-secondary" size="md">
              {highSevPriceWarnings.length > 1
                ? t("multipleBelowFloorWarning", {
                    count: highSevPriceWarnings.length,
                  })
                : t("singleBelowFloorWarning", {
                    price: highSevPriceWarnings[0].price,
                    symbol: listingSymbol,
                    floorPrice: highSevPriceWarnings[0].floorPrice
                      ? bn(highSevPriceWarnings[0].floorPrice)
                          .decimalPlaces(LISTING_MAX_DECIMALS)
                          .toString()
                      : "",
                  })}
            </TextBody>
          </ModalBody>
          <ModalFooter>
            <ModalFooterButton
              onClick={() => {
                setIsOpen(false)
              }}
              variant="secondary"
            >
              {t("cancelButton")}
            </ModalFooterButton>
            <ModalFooterButton
              onClick={() => {
                setIsOpen(false)
                onNext()
              }}
            >
              {t("confirmButton", { count: highSevPriceWarnings.length })}
            </ModalFooterButton>
          </ModalFooter>
        </>
      }
      onOpenChange={setIsOpen}
      open={open}
    />
  )
}
